/**
 * Dates, times and sizes the way the admin screens write them, in one module since 2026-09-24.
 *
 * Until the build round after the sketches, `AdminClientsRoute.tsx`, `AdminOperationsRoute.tsx` and
 * `ManagementPanel.tsx` each carried a copy, and their comments said the copies stood only because
 * there was no shared date module in `app/lib` to put them in. This is that module.
 */

const dateTime = new Intl.DateTimeFormat("sv-SE", { dateStyle: "short", timeStyle: "short" });
const dateOnly = new Intl.DateTimeFormat("sv-SE", { dateStyle: "short" });
const shortDate = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short" });
const shortDateYear = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short", year: "numeric" });
const timeOnly = new Intl.DateTimeFormat("sv-SE", { hour: "2-digit", minute: "2-digit" });

/** A `timestamptz`, as a short date and a time. Null for a missing or unreadable value. */
export function formatMoment(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : dateTime.format(parsed);
}

/**
 * A `date` column, built from its own parts rather than parsed.
 *
 * `new Date("2028-03-14")` is parsed as UTC midnight, so every timezone behind UTC renders it as the
 * thirteenth.
 */
export function formatDay(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!parts) {
    return null;
  }
  const parsed = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return Number.isNaN(parsed.getTime()) ? null : dateOnly.format(parsed);
}

/**
 * A moment the way a person says it in a list: "I dag 09:14", "I går 16:12", "22 sep 11:20", and the
 * year only when it is not this one. `now` is a parameter so a test can pin it.
 */
export function formatWhen(value: string | null | undefined, now: Date = new Date()): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const that = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  const days = Math.round((today - that) / 86_400_000);
  const time = timeOnly.format(parsed);

  if (days === 0) {
    return `I dag ${time}`;
  }
  if (days === 1) {
    return `I går ${time}`;
  }
  const date = parsed.getFullYear() === now.getFullYear() ? shortDate.format(parsed) : shortDateYear.format(parsed);
  return `${date} ${time}`;
}

/** A file size, in the Swedish form. `attachments_byte_size_check` caps an upload at 20 MB. */
export function formatBytes(value: number): string {
  const mib = value / (1024 * 1024);
  if (mib >= 1) {
    return `${mib.toFixed(1).replace(".", ",")} MB`;
  }
  return `${Math.round(value / 1024)} kB`;
}

const wholeKronor = new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0, useGrouping: true });

/**
 * Whole kronor, "3 500 kr", the way `app/lib/facts/words.ts` writes a price on a client's site: a
 * space between thousands and no decimals. Build round 25 (2026-10-09), for the agreed monthly price
 * of a management period, which `management_period_prices` holds as an integer.
 */
export function formatKronor(value: number): string {
  return `${wholeKronor.format(value)} kr`;
}
